import {createContext, memo, ReactNode, useContext} from 'react'
import type {SitePreferencesResponse} from '../helpers/customSitePreferences'
import {invariant} from '../helpers/invariant'
import {Mapping, InferredPreferences} from './site-preferences.types'
import {transformPreferences} from '../helpers/transformPreferences'
import React from 'react'

/*
 * !!! DANGER !!!
 * Usage Example:
 * Exposing site preference mappings will leak it to the browser (client side)
 * Add more groups and their mappings here as needed
 *
 * * 1. Extend PREFERENCE_MAPPING with what preferences you want to use
 * Example:
 * CHECKOUT: {
 * * ^ site preference group id (exact match)
 *     enableGuestCheckout: {
 *         id: 'enableGuestCheckout',
 *         type: 'boolean',
 *         defaultValue: false
 *     },
 *     maxItemsInCart: {
 *         id: 'maxItemsInCart',
 *         type: 'number',
 *         defaultValue: 100
 *     }
 * }
 * * 2. Use preferences in components:
 * const MyComponent = () => {
 *     const prefs = useClientSitePreferences()
 *     const expiryDays = prefs.TPA.balanceTrackerExpiryDays
 *
 *     return <div>Balance tracker expires in {expiryDays} days</div>
 * }
 * * 3. Always check if data is present and handle misconfigurations
 * const MyComponent = () => {
 *     const prefs = useClientSitePreferences()
 *     const expiryDays = prefs.TPA.balanceTrackerExpiryDays
 *
 *     invariant(typeof expiryDays === "number", "Expected expiryDays to be number")
 *
 *     return <div>Balance tracker expires in {expiryDays} days</div>
 * }
 */

// Define the mapping with proper typing using satisfies
export const PREFERENCE_MAPPING = {
    TPA: {
        balanceTrackerExpiryDays: {
            id: 'balanceTrackerExpiryDays',
            type: 'number',
            defaultValue: 0
        }
    },
    LMN: {
        lmnListAllowsTPA: {
            id: 'lmnListAllowsTPA',
            type: 'string',
            defaultValue: ''
        }
    },
    CloudFlareConfigs: {
        cloudFlareTurnstileSiteKey: {
            id: 'cloudFlareTurnstileSiteKey',
            type: 'string',
            defaultValue: ''
        },
        cloudFlareTurnstileEnabled: {
            id: 'cloudFlareTurnstileEnabled',
            type: 'boolean',
            defaultValue: false
        }
    }
} as const satisfies Mapping

// Type-safe client preferences inferred from the mapping
export type ClientSitePrefs = InferredPreferences<typeof PREFERENCE_MAPPING>

// #region Context
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const ClientSitePreferecesContext = createContext<ClientSitePrefs>(undefined!)
const ClientSitePreferencesProvider = ClientSitePreferecesContext.Provider
export const useClientSitePreferences = (): ClientSitePrefs => {
    const prefs = useContext(ClientSitePreferecesContext)
    invariant(prefs, 'useClientSitePreferences must be used within a ClientSitePreferencesProvider')
    return prefs
}
export const ClientSitePreferences = memo(
    ({preferences, children}: {preferences: ClientSitePrefs; children: ReactNode}) => {
        return (
            <ClientSitePreferencesProvider value={preferences}>
                {children}
            </ClientSitePreferencesProvider>
        )
    }
)
ClientSitePreferences.displayName = 'ClientSitePreferences'
// #endregion Context

export const toClientPreferences = (prefs: SitePreferencesResponse): ClientSitePrefs => {
    return transformPreferences<ClientSitePrefs>(prefs, PREFERENCE_MAPPING as Mapping, 'Site')
}
